import React, {useEffect, useMemo, useState} from 'react';
import {flushSync} from 'react-dom';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import type {
  JobHazardAnalysisRequest,
  JobHazardAnalysisResponse,
  JobHazardAnalysisStepResponse,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  JobHazardAnalysisStateEnum,
  RiskAssessmentCodeAreaEnum,
} from 'src/api/ProactApiClient';
import {
  useDeleteJobHazardAnalysisMutation,
  useGetJobHazardAnalysisQuery,
  useUpdateJobHazardAnalysisMutation,
} from 'src/redux/queries/jobHazardAnalysis';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SidebarLinkJobHazardAnalysis} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import type {JobHazardAnalysisFormValues} from 'src/app/job-hazard-analysis/JobHazardAnalysisForm';
import {
  JobHazardAnalysisForm,
  useJobHazardAnalysisForm,
} from 'src/app/job-hazard-analysis/JobHazardAnalysisForm';
import {
  JobHazardAnalysisPillConfig,
  jhaDocumentTitle,
  jobStepsMeta,
  pageTitleDict,
} from 'src/app/job-hazard-analysis/jobHazardAnalysisHelpers';
import {JobHazardAnalysisStepModal} from 'src/app/job-hazard-analysis/JobHazardAnalysisStepModal';
import {JobHazardAnalysisStepList} from 'src/app/job-hazard-analysis/JobHazardAnalysisStepList';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetRiskAssessmentCodeByAreaQuery} from 'src/redux/queries/riskAsessmentCode';
import {useJHAPermissions} from 'src/shared/auth/permissions/useJHAPermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {v4 as uuidv4} from 'uuid';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: pageTitleDict.singular,
  entityNamePlural: pageTitleDict.plural,
  listPath: PathUtility.JobHazardAnalysis(),
  viewPath: id => PathUtility.JobHazardAnalysisView(String(id), 'content'),
};

export const EditJobHazardAnalysisPage: React.FC = () => {
  useSetDocumentTitle(jhaDocumentTitle);
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const {id} = useParams();
  const jhaPerms = useJHAPermissions();
  const [item, setItem] = useState<JobHazardAnalysisResponse | undefined>(
    undefined
  );
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteStepId, setDeleteStepId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [isSavePending, setIsSavePending] = useState(false);

  const [updateJobHazardAnalysis] = useUpdateJobHazardAnalysisMutation();
  const [deleteJobHazardAnalysis] = useDeleteJobHazardAnalysisMutation();

  const form = useJobHazardAnalysisForm();
  const {handleSubmit: handleFormSubmit, reset, getValues} = form;
  const [open, setOpen] = useState(false);
  const [jobStepsList, setJobStepsList] = useState<
    JobHazardAnalysisStepResponse[]
  >([]);
  const [editingStep, setEditingStep] = useState<JobHazardAnalysisStepResponse>(
    () => ({...jobStepsMeta, offlineId: uuidv4()})
  );

  const {data: riskAsset, isLoading: racIsLoading} =
    useGetRiskAssessmentCodeByAreaQuery(RiskAssessmentCodeAreaEnum.Safety);

  const workflowId = parseInt(id ?? '-1');

  const {
    data: fetchedJha,
    isLoading,
    error: fetchError,
  } = useGetJobHazardAnalysisQuery(workflowId, {skip: workflowId < 0});

  useEffect(() => {
    if (!fetchedJha) return;

    if (fetchedJha.state !== JobHazardAnalysisStateEnum.Draft) {
      setAlert('This JHA cannot be edited in its current state', 'error');
      navigate(
        PathUtility.JobHazardAnalysisView(String(fetchedJha.id), 'content')
      );
      return;
    }

    setItem(fetchedJha);
    reset({
      subjectName: fetchedJha.subjectName ?? '',
      location: fetchedJha.location ?? '',
      workStartDate: fetchedJha.workStartDate
        ? new Date(fetchedJha.workStartDate)
        : null,
      workFinishDate: fetchedJha.workFinishDate
        ? new Date(fetchedJha.workFinishDate)
        : null,
    });

    const steps = fetchedJha.jobHazardAnalysisSteps.map(step => ({
      ...step,
      offlineId: uuidv4(),
    }));
    setJobStepsList(steps);
  }, [fetchedJha, reset]);

  useEffect(() => {
    if (fetchError) {
      const message = parseErrorMessage(fetchError);
      setAlert(message, 'error');
      setLoadError(message);
    }
  }, [fetchError]);

  const handleSaveJobStep = (stepToSave: JobHazardAnalysisStepResponse) => {
    const exists = jobStepsList.some(
      obj => obj.offlineId === stepToSave.offlineId
    );

    if (exists)
      setJobStepsList(prevState =>
        prevState.map(s =>
          s.offlineId === stepToSave.offlineId ? stepToSave : s
        )
      );
    else setJobStepsList(prevState => [...prevState, stepToSave]);

    setOpen(false);
  };

  const handleSave = async (values: JobHazardAnalysisFormValues) => {
    try {
      if (!riskAsset) {
        return;
      }

      setIsSavePending(true);

      const body: JobHazardAnalysisRequest = {
        id: workflowId,
        location: values.location,
        riskAssessmentCodeId: riskAsset.id,
        workStartDate: values.workStartDate,
        workFinishDate: values.workFinishDate,
        subjectName: values.subjectName,
        racBefore: item?.racBefore ?? 0,
        racAfter: item?.racAfter ?? 0,
        racWeighted: item?.racWeighted ?? 0,
        targetJobHazardAnalysisSteps: jobStepsList.map(jobStep => ({
          id: jobStep.id,
          name: jobStep.name,
          potentialHazard: jobStep.potentialHazard,
          racBeforeControls: jobStep.racBeforeControls,
          racAfterControls: jobStep.racAfterControls,
          racMatrix: jobStep.racMatrix ?? 0,
          riskAfterProbability: jobStep.riskAfterProbability,
          riskAfterSeverity: jobStep.riskAfterSeverity,
          riskBeforeProbability: jobStep.riskBeforeProbability,
          riskBeforeSeverity: jobStep.riskBeforeSeverity,
          percentOfOverallWork: jobStep.percentOfOverallWork ?? 0,
          safetyControls: jobStep.safetyControls.map(safetyControl => ({
            id: safetyControl.id,
            safetyControlsTitle: safetyControl.safetyControlsTitle,
            safetyNotes: safetyControl.safetyNotes,
          })),
        })),
      };

      const resp = await updateJobHazardAnalysis(body).unwrap();

      flushSync(() => {
        reset(getValues());
      });
      setAlert(
        `${
          resp.subjectName ?? pageTitleDict.singular
        } was successfully updated`,
        'success'
      );

      navigate(PathUtility.JobHazardAnalysisView(String(resp.id), 'content'), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = handleFormSubmit(handleSave);

  const {goBack: handleCancel} = useBackNavigation(
    PathUtility.JobHazardAnalysisView(id!, 'content')
  );

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode: 'edit',
        id,
        itemName: item?.subjectName,
      }),
    [id, item?.subjectName]
  );

  const pills = JobHazardAnalysisPillConfig(item?.state);

  const handleConfirmDelete = async () => {
    try {
      setIsSavePending(true);

      await deleteJobHazardAnalysis(+id!).unwrap();

      setAlert(
        `${
          item?.subjectName ?? pageTitleDict.singular
        } was successfully deleted`,
        'success'
      );

      navigate(PathUtility.JobHazardAnalysis());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleClickOpen = () => {
    setEditingStep({
      ...jobStepsMeta,
      jobHazardAnalysisId: +id!,
      offlineId: uuidv4(),
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEditJobStep = (offlineId?: string) => {
    const stepToEdit = jobStepsList.find(step => step.offlineId === offlineId);

    if (stepToEdit) {
      setEditingStep(stepToEdit);
      setOpen(true);
    }
  };

  const handleJobStepDelete = (offlineId?: string) => {
    if (offlineId) {
      setDeleteStepId(offlineId);
    }
  };

  const handleConfirmStepDelete = () => {
    setJobStepsList(
      jobStepsList.filter(step => step.offlineId !== deleteStepId)
    );
    setDeleteStepId(null);
  };

  const deleteStepName = deleteStepId
    ? jobStepsList.find(s => s.offlineId === deleteStepId)?.name ?? 'this step'
    : '';

  const isLoaderVisible = isLoading || racIsLoading;

  if (loadError && !item) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
        <Stack gap={0}>
          <BackBar
            fallbackTo={navigation.fallbackTo}
            fallbackLabel={navigation.fallbackLabel}
            breadcrumbs={navigation.breadcrumbs}
          />
          <Alert color="red" mt="sm" mx="md">
            {loadError}
          </Alert>
        </Stack>
      </ContentWrapper>
    );
  }

  if (!jhaPerms.update) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
        <Stack gap={0}>
          <BackBar
            fallbackTo={navigation.fallbackTo}
            fallbackLabel={navigation.fallbackLabel}
            breadcrumbs={navigation.breadcrumbs}
          />
          <Alert color="red" mt="sm" mx="md">
            You do not have permission to edit Job Hazard Analyses.
          </Alert>
        </Stack>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      {isLoaderVisible ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : (
        <>
          <PageHeader>
            <PageHeader.Title icon={SidebarLinkJobHazardAnalysis.icon}>
              {item?.subjectName}
            </PageHeader.Title>
            {item?.internalIdentifier && (
              <PageHeader.Meta>{item.internalIdentifier}</PageHeader.Meta>
            )}
            <PageHeader.Badges>
              {pills.map(pill => (
                <PageHeader.Badge
                  key={pill.text}
                  color={pill.color}
                  variant="light">
                  {pill.text}
                </PageHeader.Badge>
              ))}
              <PageHeader.Badge color="yellow" variant="light">
                Editing
              </PageHeader.Badge>
            </PageHeader.Badges>
            <PageHeader.Actions>
              <PageHeader.Action variant="cancel" onClick={handleCancel}>
                Cancel
              </PageHeader.Action>
              <PageHeader.Action
                variant="save"
                loading={isSavePending}
                onClick={handleValidateAndSave}>
                Save
              </PageHeader.Action>
              {item?.state === JobHazardAnalysisStateEnum.Draft &&
                jhaPerms.delete && (
                  <PageHeader.Action
                    variant="delete"
                    onClick={() => setShowConfirmDelete(true)}>
                    Delete
                  </PageHeader.Action>
                )}
            </PageHeader.Actions>
          </PageHeader>

          <Stack gap="md">
            <JobHazardAnalysisForm form={form} mode="edit" />

            <SectionCard
              title="Job Steps"
              rightSection={
                <CreateButton size="sm" onClick={handleClickOpen}>
                  Add Step
                </CreateButton>
              }>
              <Box p="md">
                <JobHazardAnalysisStepList
                  steps={jobStepsList}
                  riskMatrix={riskAsset}
                  onStepDelete={handleJobStepDelete}
                  mode="edit"
                  onStepEdit={handleEditJobStep}
                />
              </Box>
            </SectionCard>
          </Stack>

          <Group mt="md">
            <PageHeader.Action variant="cancel" onClick={handleCancel}>
              Cancel
            </PageHeader.Action>
            <PageHeader.Action
              variant="save"
              loading={isSavePending}
              onClick={handleValidateAndSave}>
              Save
            </PageHeader.Action>
          </Group>

          {riskAsset && (
            <JobHazardAnalysisStepModal
              open={open}
              step={editingStep}
              riskAsset={riskAsset}
              handleClose={handleClose}
              onSave={handleSaveJobStep}
            />
          )}
          <ConfirmDialog
            opened={deleteStepId !== null}
            onClose={() => setDeleteStepId(null)}
            onConfirm={handleConfirmStepDelete}
            title="Delete Job Step"
            body={`Are you sure you want to delete "${deleteStepName}"? This will remove the step and all its safety controls.`}
            confirmationButtonColor="red"
            buttonTextYes="Delete"
            buttonTextNo="Cancel"
          />
          <ConfirmDialog
            opened={showConfirmDelete}
            title="Confirm Delete"
            body="Are you sure you want to delete this item?"
            buttonTextYes="Yes, I'm sure"
            onClose={() => setShowConfirmDelete(false)}
            onConfirm={handleConfirmDelete}
            size="md"
          />
        </>
      )}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.JobHazardAnalysisEdit(':id'),
  element: <EditJobHazardAnalysisPage />,
  isPrivate: true,
});
